// The Match Results live adapter (Track 2, T2-2): app-api's `/seasons` + `/matches` → the
// same ProtoFixture[] the snapshot hook returns, so the widget doesn't change. The league
// list is League Table's own (`loadLeagueChoices`, league.ts) — the two widgets switch into
// each other, so they must list the same leagues.

import type { MatchSummary, MatchesResponse, ProtoFixture, SeasonsResponse } from "@catbento/shared";
import type { LiveApi } from "./api.js";
import type { LeagueChoice } from "../data/league.js";

/**
 * The played matches only (decided 2026-10-05, fork 2A): a fixture not played yet has null
 * scores, and the widget — like the snapshot it was built on — only shows results. Whether
 * to show upcoming fixtures too is a question for Ben (a new look).
 */
export function toProtoFixtures(matches: MatchSummary[], seasonCode?: string): ProtoFixture[] {
  const out: ProtoFixture[] = [];
  for (const m of matches) {
    if (m.homeScore === null || m.awayScore === null) continue;
    out.push({
      matchId: m.matchId,
      matchDate: m.matchDate,
      homeTeamName: m.homeTeamName,
      awayTeamName: m.awayTeamName,
      homeScore: m.homeScore,
      awayScore: m.awayScore,
      homeTeamId: m.homeTeamId,
      awayTeamId: m.awayTeamId,
      ...(seasonCode !== undefined ? { seasonCode } : {}),
    });
  }
  return out;
}

/**
 * A league's results for its newest season with a played match (fork 1A — the same rule
 * League Table opens on, so Table → Results shows the same season by default). The widget
 * has no season picker; the snapshot held one season per league. `/seasons` lists the
 * league's seasons newest first (by its own latest match), so a brand-new season with only
 * fixtures is skipped until its first match is played.
 */
export async function loadMatchResults(api: LiveApi, league: LeagueChoice): Promise<ProtoFixture[]> {
  const { seasons } = await api.get<SeasonsResponse>(`/seasons?competitionId=${league.id}`);
  for (const s of seasons) {
    const { matches } = await api.get<MatchesResponse>(`/matches?competitionId=${league.id}&seasonId=${s.id}`);
    const played = toProtoFixtures(matches, String(s.id));
    if (played.length) return played;
  }
  // No season with a played match (e.g. a cup with only fixtures): the widget's own
  // "No match results captured in …" message.
  return [];
}

/** One league season's played matches — the League Table's form guide for the season it shows
 *  (Ben, 2026-10-18). */
export async function loadSeasonResults(api: LiveApi, competitionId: number, seasonCode: string): Promise<ProtoFixture[]> {
  const { matches } = await api.get<MatchesResponse>(`/matches?competitionId=${competitionId}&seasonId=${seasonCode}`);
  return toProtoFixtures(matches);
}

/** One league season's matches, played and still to come — the Bio's team card (its form, last
 *  result and next game, teams and matches 2026-10-30). */
export async function loadSeasonMatches(api: LiveApi, competitionId: number, seasonCode: string): Promise<MatchSummary[]> {
  const { matches } = await api.get<MatchesResponse>(`/matches?competitionId=${competitionId}&seasonId=${seasonCode}`);
  return matches;
}
